// ** Modules
import { useState } from 'react';
import { useReducedMotion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';

// ** Local
import type { MenuViewProps } from './megaMenuView.types';
import MegaMenuContactFooter from './MegaMenuContactFooter';
import MobileAccordionSection from './MobileAccordionSection';

const HOVER_UNDERLINE_SX = {
	textDecoration: 'underline',
	textDecorationColor: 'transparent',
	textUnderlineOffset: '3px',
	transition: 'text-decoration-color 0.1s ease',
	'&:hover': {
		textDecorationColor: 'rgba(255, 255, 255, 0.65)'
	}
} as const;

const MegaMenuDivider = () => {
	const theme = useTheme();
	return (
		<Box
			sx={{
				borderTop: `1px solid ${alpha(theme.palette.common.white, 0.18)}`
			}}
		/>
	);
};

const MenuView = ({ memberLinks, menuColumns, contact, socialLinks, isLoggedIn = true }: MenuViewProps) => {
	const [expandedKey, setExpandedKey] = useState<string | null>(null);
	const shouldReduceMotion = useReducedMotion();
	const mobileAccordionPanelTransition = shouldReduceMotion
		? { duration: 0 }
		: {
				height: { duration: 0.55, ease: [0.4, 0, 0.2, 1] as const },
				opacity: { duration: 0.4, ease: 'easeInOut' as const }
			};
	const colCount = menuColumns.length;

	return (
		<Box sx={{ width: '100%' }}>
			{/* ── Desktop ── */}
			<Box
				sx={{
					display: { xs: 'none', md: 'flex' },
					flexDirection: 'column',
					pb: 16
				}}
			>
				{isLoggedIn ? (
					<>
						<Box>
							<Typography component="h3" variant="nav" sx={{ color: 'common.white' }}>
								Mitgliederbereich
							</Typography>
							<Box
								sx={{
									mt: 6,
									display: 'flex',
									flexWrap: 'wrap',
									gap: 16
								}}
							>
								{memberLinks.map((item) => (
									<Link key={item.label} href={item.href} passHref>
										<Typography
											component="span"
											variant="body-md"
											sx={{
												color: 'common.white',
												fontSize: '18px',
												lineHeight: '25px',
												...HOVER_UNDERLINE_SX
											}}
										>
											{item.label}
										</Typography>
									</Link>
								))}
							</Box>
						</Box>

						<Box sx={{ mt: 12 }}>
							<MegaMenuDivider />
						</Box>
					</>
				) : null}

				<Box sx={{ mt: isLoggedIn ? 12 : 0 }}>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: `repeat(${Math.min(3, colCount)}, auto)`,
							'@media (min-width:992px)': {
								gridTemplateColumns: `repeat(${Math.min(4, colCount)}, auto)`
							},
							'@media (min-width:1100px)': {
								gridTemplateColumns: `repeat(${Math.min(5, colCount)}, auto)`
							},
							'@media (min-width:1280px)': {
								gridTemplateColumns: `repeat(${Math.min(6, colCount)}, auto)`
							},
							justifyContent: colCount >= 5 ? 'space-between' : 'start',
							columnGap: colCount >= 5 ? 'clamp(16px, 2vw, 32px)' : 'clamp(32px, 5vw, 80px)',
							rowGap: 'clamp(24px, 4vw, 48px)',
							alignItems: 'start'
						}}
					>
						{menuColumns.map((column) => (
							<Box key={column.title}>
								<Typography
									component="h3"
									variant="nav"
									sx={{
										color: 'common.white',
										whiteSpace: 'nowrap',
										overflow: 'visible'
									}}
								>
									{column.title}
								</Typography>
								<Stack spacing={4.5} sx={{ mt: 6 }}>
									{column.items.map((item) => (
										<Link key={`${column.title}-${item.label}`} href={item.href} passHref>
											<Typography
												component="span"
												variant="subnav"
												sx={{
													color: 'common.white',
													...HOVER_UNDERLINE_SX
												}}
											>
												{item.label}
											</Typography>
										</Link>
									))}
								</Stack>
							</Box>
						))}
					</Box>
				</Box>

				<Box sx={{ mt: 12 }}>
					<MegaMenuDivider />
				</Box>

				<Box sx={{ mt: 12 }}>
					<MegaMenuContactFooter contact={contact} socialLinks={socialLinks} />
				</Box>
			</Box>

			{/* ── Mobile ── */}
			<Box
				sx={{
					display: { xs: 'flex', md: 'none' },
					flexDirection: 'column',
					width: '100%',
					flex: 1,
					minHeight: 0,
					pb: 12
				}}
			>
				{isLoggedIn ? (
					<MobileAccordionSection
						title="Mitgliederbereich"
						items={memberLinks}
						sectionKey="member"
						panelId="mega-menu-mobile-panel-member"
						isExpanded={expandedKey === 'member'}
						onToggle={() => setExpandedKey((k) => (k === 'member' ? null : 'member'))}
						transition={mobileAccordionPanelTransition}
					/>
				) : null}

				{menuColumns.map((column, columnIndex) => {
					const sectionKey = `col-${columnIndex}`;
					return (
						<MobileAccordionSection
							key={column.title}
							title={column.title}
							items={column.items}
							sectionKey={sectionKey}
							panelId={`mega-menu-mobile-panel-${columnIndex}`}
							isExpanded={expandedKey === sectionKey}
							onToggle={() => setExpandedKey((k) => (k === sectionKey ? null : sectionKey))}
							transition={mobileAccordionPanelTransition}
						/>
					);
				})}

				<Box sx={{ mt: 12 }}>
					<MegaMenuContactFooter contact={contact} socialLinks={socialLinks} mobile />
				</Box>
			</Box>
		</Box>
	);
};

export default MenuView;
